<template>
  <div :class="[classes]">
    <input type="file" :accept="accept" @change="onFileChange" class="hidden" ref="fileInput" />
    <div class="cursor-pointer">
      <ImageCropper
        v-if="fileUrl && accept.includes('image')"
        :imageUrl="fileUrl"
        :crop="crop"
        @updateImageCrop="updateImageCrop"
      />
      <div
        v-else
        class="w-full h-96 bg-surface-primary border border-linear-subtle rounded-md relative"
        @click="clickOnFileInput"
      >
        <PlaceholderBox />
      </div>
    </div>
    <div class="relative flex justify-end items-center mt-2 space-x-4 pl-12">
      <label
        id="file-upload-label"
        class="font-label text-fg-secondary text-uppercase text-xs absolute left-0 top-0 uppercase"
      >
        {{ label }}
      </label>
      <div class="flex flex-col">
        <span>{{ imageName }}</span>
        <span
          v-show="showImageBigSizeAlert"
          class="text-alerts-warning text-sm"
          id="image-size-alert"
        >
          That image is too large.
        </span>
      </div>
      <ButtonDefault label="Select" @click="clickOnFileInput" alt />
    </div>
    <span
      class="text-xs text-fg-secondary inline-block mt-2"
      v-show="helperText"
      id="file-input-helper-text"
    >
      {{ helperText }}
    </span>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue'

import PlaceholderBox from '@/assets/placeholderBox.svg?component'
import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ImageCropper from '@/common/components/molecules/ImageCropper.vue'
import { ShowImageCrop, position2crop } from '@/models'

export default defineComponent({
  name: 'FileUploadInput',
  components: { ImageCropper, ButtonDefault, PlaceholderBox },
  model: {
    prop: 'file',
    event: 'input',
  },
  emits: {
    // eslint-disable-next-line no-unused-vars
    updateImageCrop: (crop: ShowImageCrop) => true,
    // eslint-disable-next-line no-unused-vars
    input: (file: File | null) => true,
  },
  props: {
    file: {
      type: Object as PropType<File | null>,
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    classes: {
      type: String,
      default: '',
    },
    initialImageUrl: {
      type: String,
      default: '',
    },
    crop: {
      type: Object as PropType<ShowImageCrop>,
      default: () => position2crop([]),
    },
    accept: {
      type: String,
      required: true,
    },
    helperText: {
      type: String,
      default: '',
    },
    maxWidth: {
      type: Number,
    },
    maxHeight: {
      type: Number,
    },
  },
  data() {
    return {
      fileName: '',
      fileUrl: '',
      showImageBigSizeAlert: false,
    }
  },
  methods: {
    onFileChange(e: Event) {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        this.$emit('input', file)
        this.fileName = file.name
        this.fileUrl = URL.createObjectURL(file)
      } else {
        this.resetFile()
      }
    },
    clickOnFileInput() {
      const fileInput = this.$refs.fileInput as HTMLInputElement
      fileInput.click()
    },
    updateImageCrop(crop: ShowImageCrop) {
      this.$emit('updateImageCrop', crop)
    },
    resetFile() {
      this.$emit('input', null)
      this.fileName = ''
      this.fileUrl = ''
    },
    validateImageDimensions(): void {
      if (!this.accept.includes('image')) return
      if (!this.maxWidth || !this.maxHeight) return

      const self = this
      const img = new Image()
      img.onload = function () {
        const image = this as HTMLImageElement
        self.showImageBigSizeAlert =
          image.width > (self.maxWidth as number) || image.height > (self.maxHeight as number)
      }
      img.src = this.fileUrl
    },
  },
  computed: {
    imageName(): string {
      return this.fileName || 'No file selected'
    },
  },
  watch: {
    initialImageUrl(newUrl: string) {
      this.fileUrl = newUrl
    },
    fileUrl(newUrl: string) {
      if (!newUrl) return
      this.validateImageDimensions()
    },
    showImageBigSizeAlert(newAlert: boolean) {
      if (newAlert) this.resetFile()
    },
  },
})
</script>
